import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

// Six of these endpoints (below) are shared between the anonymous checkout
// SPA and this app's own authenticated admin UI (archive/unarchive, the
// edit modal, the admin Detail page) - see middleware/checkoutSessionAuth.js
// on the backend. The admin calls already carry a real Authorization
// header automatically (this app's shared baseQuery attaches it whenever
// someone's logged in) and need no changes; an anonymous checkout-SPA call
// has no such header, so it must send the checkout session token
// (useSelfServeCheckout.js's `sessionToken`, minted by createSelfserveUser/
// verifyAndRedirect) as this header instead. `withSessionToken` builds that
// header only when a token is actually passed, so every existing admin call
// site - which never passes one - is unaffected.
const withSessionToken = (sessionToken) =>
  sessionToken ? { headers: { "x-checkout-session-token": sessionToken } } : {};

export const integrationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSelfServeCheckoutBundles: builder.query({
      query: (params) => ({
        url: apiRoutes.selfServeCheckouts.getBundles.path,
        method: apiRoutes.selfServeCheckouts.getBundles.reqType,
        params,
      }),
    }),
    // Signup stat cards and funnel tab counts, computed over every signup on the server.
    getSelfServeOverview: builder.query({
      query: () => ({
        url: apiRoutes.selfServeCheckouts.overview.path,
        method: apiRoutes.selfServeCheckouts.overview.reqType,
      }),
    }),
    getSelfServeCheckoutUsers: builder.query({
      query: (params) => ({
        url: apiRoutes.selfServeCheckouts.getSelfServeUsers.path,
        method: apiRoutes.selfServeCheckouts.getSelfServeUsers.reqType,
        params,
      }),
    }),
    getSelfServeCheckoutConfig: builder.query({
      query: () => ({
        url: apiRoutes.selfServeCheckouts.getConfigUrls.path,
        method: apiRoutes.selfServeCheckouts.getConfigUrls.reqType,
      }),
    }),
    updateConfigUrls: builder.mutation({
      query: (data) => ({
        url: apiRoutes.selfServeCheckouts.updateConfigurls.path,
        method: apiRoutes.selfServeCheckouts.updateConfigurls.reqType,
        body: data,
      }),
    }),
    updateBillingCycles: builder.mutation({
      query: (data) => ({
        url: apiRoutes.selfServeCheckouts.updateBillingCycles.path,
        method: apiRoutes.selfServeCheckouts.updateBillingCycles.reqType,
        body: data,
      }),
    }),
    createSubscriptionFromProductLed: builder.mutation({
      query: ({ sessionToken, ...data } = {}) => ({
        url: apiRoutes.selfServeCheckouts.createSubscription.path,
        method: apiRoutes.selfServeCheckouts.createSubscription.reqType,
        body: data,
        ...withSessionToken(sessionToken),
      }),
    }),
    // The trial-signup counterpart (no payment) and its optional card-
    // capture pair - the authenticated /subscription/:id/payment-method/*
    // endpoints (_features/subscriptions/api/subscriptionApi.js) require a
    // logged-in JWT, which a visitor mid self-serve checkout doesn't have
    // yet, so these public routes (routePermissions.js: permission null,
    // scoped server-side to the caller's own orgId + a still-Trialing
    // subscription) exist instead.
    createTrialSubscriptionFromProductLed: builder.mutation({
      query: ({ sessionToken, ...data } = {}) => ({
        url: apiRoutes.selfServeCheckouts.createTrialSubscription.path,
        method: apiRoutes.selfServeCheckouts.createTrialSubscription.reqType,
        body: data,
        ...withSessionToken(sessionToken),
      }),
    }),
    createTrialSetupIntent: builder.mutation({
      query: ({ subscriptionId, orgId, email }) => ({
        url: `${apiRoutes.selfServeCheckouts.createTrialSetupIntent.path}${subscriptionId}/payment-method/setup-intent`,
        method: apiRoutes.selfServeCheckouts.createTrialSetupIntent.reqType,
        body: { orgId, email },
      }),
    }),
    confirmTrialPaymentMethod: builder.mutation({
      query: ({ subscriptionId, orgId, setupIntentId }) => ({
        url: `${apiRoutes.selfServeCheckouts.confirmTrialPaymentMethod.path}${subscriptionId}/payment-method/confirm`,
        method: apiRoutes.selfServeCheckouts.confirmTrialPaymentMethod.reqType,
        body: { orgId, setupIntentId },
      }),
    }),
    logSelfServeUserStage: builder.mutation({
      query: ({ sessionToken, ...data } = {}) => ({
        url: apiRoutes.selfServeCheckouts.logSelfServeUserStage.path,
        method: apiRoutes.selfServeCheckouts.logSelfServeUserStage.reqType,
        body: data,
        ...withSessionToken(sessionToken),
      }),
    }),
    createSelfserveUser: builder.mutation({
      query: (data) => ({
        url: apiRoutes.selfServeCheckouts.createSelfserveuser.path,
        method: apiRoutes.selfServeCheckouts.createSelfserveuser.reqType,
        body: data,
      }),
    }),
  editSelfserveUser: builder.mutation({
      query: ({ sessionToken, ...data } = {}) => ({
        url: apiRoutes.selfServeCheckouts.editSelfserveuser.path,
        method: apiRoutes.selfServeCheckouts.editSelfserveuser.reqType,
        body: data,
        ...withSessionToken(sessionToken),
      }),
    }),
    // `arg` is either a raw SelfserveUsers id string (every existing
    // authenticated-admin call site - SelfServeUserDetails' page, this
    // hook's own admin-adjacent uses) or, from the anonymous checkout SPA,
    // `{ id, sessionToken }` so the session token can travel as a header.
    getSelfserveUserById: builder.mutation({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg?.id;
        const sessionToken = typeof arg === "string" ? undefined : arg?.sessionToken;
        return {
          url: `${apiRoutes.selfServeCheckouts.getAllSelfServeUserById.path}/${id}`,
          method: apiRoutes.selfServeCheckouts.getAllSelfServeUserById.reqType,
          ...withSessionToken(sessionToken),
        };
      },
    }),
    createCompanyFromSelfServeUser: builder.mutation({
      query: ({ sessionToken, ...data } = {}) => ({
        url: apiRoutes.selfServeCheckouts.createCompanyFormSelfserveUser.path,
        method: apiRoutes.selfServeCheckouts.createCompanyFormSelfserveUser.reqType,
        body: data,
        ...withSessionToken(sessionToken),
      }),
    }),
  }),
});

export const {
  useGetSelfServeCheckoutUsersQuery,
  useGetSelfServeOverviewQuery,
  useCreateSubscriptionFromProductLedMutation,
  useCreateTrialSubscriptionFromProductLedMutation,
  useCreateTrialSetupIntentMutation,
  useConfirmTrialPaymentMethodMutation,
  useGetSelfServeCheckoutBundlesQuery,
  useUpdateConfigUrlsMutation,
  useGetSelfServeCheckoutConfigQuery,
  useLogSelfServeUserStageMutation,
  useUpdateBillingCyclesMutation,
  useCreateSelfserveUserMutation,
  useGetSelfserveUserByIdMutation,
  useEditSelfserveUserMutation,
  useCreateCompanyFromSelfServeUserMutation
} = integrationApiSlice;
